import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { SeoService } from '../../seo/seo.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';

type Partner = {
  name: string;
  logo: string;
  url: string;
  description: string;
};

@Component({
  standalone: true,
  selector: 'app-home',
  imports: [CommonModule, RouterModule],
  templateUrl: './home.html',
  styleUrls: ['./home.scss'],
})
export class HomeComponent implements OnInit {
  services = [
    { title: 'Sanificazioni', img: 'assets/sanificazione.webp', link: '/sanificazioni' },
    { title: 'Pulizia Palestre', img: 'assets/pulizia-palestre.webp', link: '/palestra' },
    { title: 'Pulizia Condomini', img: 'assets/pulizia-condomini.webp', link: '/condomini' },
    { title: 'Pulizia Uffici', img: 'assets/pullizia-uffici-postazione.webp', link: '/uffici' },
    { title: 'Pulizie Domestiche', img: 'assets/pulizia-domestica.webp', link: '/domestica' },
    { title: 'Pulizie Straordinarie', img: 'assets/pulizia-straordinaria.webp', link: '/straordinaria' },
    { title: 'Pulizie Laboratori Alimentari', img: 'assets/pastificio.webp', link: '/laboratori-alimentari' },
    { title: 'Pulizie Cucine Industriali', img: 'assets/panificio.webp', link: '/cucine-industriali' }
  ];

  // ✅ Collaboratori (stesso array della pagina Collaboratori)
  partners: Partner[] = [
    {
      name: 'Di Renzo',
      logo: 'assets/partners/direnzo.webp',
      url: 'https://www.direnzoassicurazioni.it/',
      description: 'Realtà del territorio con servizi professionali e supporto consulenziale per aziende e privati.'
    },
    {
      name: 'Europont',
      logo: 'assets/partners/europont.webp',
      url: 'https://www.europont.net/',
      description: 'Servizi dedicati al settore edile, con attività operative e soluzioni su cantiere.'
    },
    {
      name: 'Eurotek',
      logo: 'assets/partners/eurotek.webp',
      url: 'https://www.eurotekpescara.it/',
      description: 'Soluzioni e supporto tecnico per aziende, con attività rivolte a contesti professionali e industriali.'
    },
    {
      name: 'Futura',
      logo: 'assets/partners/futura.webp',
      url: 'https://www.futura-dev.com/it',
      description: 'Soluzioni digitali e servizi professionali: sviluppo, innovazione e progetti su misura.'
    },
    {
      name: 'Infobasic',
      logo: 'assets/partners/infobasic.webp',
      url: 'https://www.infobasic.it/',
      description: 'Formazione in ambito digitale e percorsi professionali, con attività e progetti sul territorio.'
    },
    {
      name: 'Italia Distribuzione Import',
      logo: 'assets/partners/italia distribuzione.webp',
      url: 'https://www.italiadistribuzione.com/',
      description: 'Distribuzione e fornitura: prodotti e soluzioni per aziende e professionisti.'
    },
    {
      name: 'Emme Ci Traslochi',
      logo: 'assets/partners/mc.webp',
      url: 'https://www.emmecitraslochi.it/',
      description: 'Traslochi e movimentazione: logistica, supporto operativo e servizi per privati e aziende.'
    },
    {
      name: 'Pluralis',
      logo: 'assets/partners/pluralis.webp',
      url: 'https://www.pluraliscoworking.com/',
      description: 'Spazi e servizi di coworking: ambienti professionali e soluzioni flessibili per lavoro e meeting.'
    },

    // ✅ NUOVI
    {
      name: 'SportAccademy',
      logo: 'assets/partners/sportaccademy.webp',
      // era: 'sportaccademy pescara' (non valido). Metto una ricerca Google finché non hai il sito ufficiale.
      url: 'https://www.google.com/search?q=SportAccademy+Pescara',
      description: 'Centro sportivo e attività fitness: servizi e percorsi dedicati al benessere e alla preparazione.'
    },
    {
      name: 'Studio Ivone',
      logo: 'assets/partners/ivone.webp',
      url: 'https://www.studioivone.it/',
      description: 'Studio professionale sul territorio: consulenza e supporto dedicato per aziende e privati.'
    },
    {
      name: 'Centro Pollio',
      logo: 'assets/partners/pollio.webp',
      url: 'https://centropollio.it/',
      description: 'Centro specializzato con servizi dedicati e attività professionali nel settore sanitario.'
    },
    {
      name: 'Santa Lucia – Centro Analisi',
      logo: 'assets/partners/santa lucia.webp',
      url: 'https://santalucialaboratorioanalisi.it/',
      description: 'Laboratorio di analisi: servizi diagnostici e supporto per prelievi e attività cliniche.'
    },
    {
      name: 'MV TechCore',
      logo: 'assets/partners/mvtechcore.webp',
      url: 'https://mvtechcore.it/',
      description: 'Agenzia che ha realizzato il sito: sviluppo, SEO tecnico, performance e gestione evolutiva.'
    },
  ];

  private isBrowser: boolean;

  constructor(
    private router: Router,
    private seo: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  // ---------- SEO ----------
  private setupSeo(): void {
    this.seo.updateTags({
      title: 'Impresa di pulizie a Pescara, Giulianova e Teramo | SAMI',
      description:
        'Pulizie professionali a Pescara. Da settembre 2026 operativi anche a Giulianova, Teramo e provincia per uffici, condomini e ogni ambiente.',
      keywords:
        'pulizie Pescara, impresa pulizie Giulianova, impresa pulizie Teramo, pulizia uffici Teramo, pulizia condomini Giulianova, sanificazioni',
      canonicalUrl: '/',

      ogTitle: 'Impresa di pulizie a Pescara, Giulianova e Teramo | SAMI',
      ogDescription:
        'Pulizie professionali a Pescara e, da settembre 2026, anche a Giulianova, Teramo e provincia.',
      ogImage: '/assets/logo.png',

      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CleaningService',
        name: 'SAMI Pulizie',
        image: 'https://samipulizie.it/assets/pullizia-uffici-postazione.webp',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via Tirino 145',
          addressLocality: 'Pescara',
          addressRegion: 'PE',
          postalCode: '65125',
          addressCountry: 'IT'
        },
        areaServed: [
          'Pescara', 'Montesilvano', 'Francavilla al Mare', 'Chieti', 'Spoltore',
          'Giulianova', 'Teramo', 'Roseto degli Abruzzi', 'Alba Adriatica', 'Tortoreto',
          'Silvi', 'Pineto', "Mosciano Sant'Angelo", 'Martinsicuro', 'Atri', 'Bellante',
          'Notaresco', 'Castellalto', "Sant'Egidio alla Vibrata"
        ],
        telephone: '+3908554360',
        email: 'info@samipulizie.it',
        department: {
          '@type': 'CleaningService',
          '@id': 'https://samipulizie.it/#giulianova',
          name: 'SAMI Pulizie - Sede di Giulianova',
          telephone: '+390859155805',
          email: 'commerciale.te@samipulizie.it',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Corso Nazario Sauro 118',
            addressLocality: 'Giulianova',
            addressRegion: 'TE',
            postalCode: '64021',
            addressCountry: 'IT'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 42.7533741,
            longitude: 13.9669969
          },
          hasMap: 'https://www.google.com/maps/search/?api=1&query=42.7533741%2C13.9669969',
          url: 'https://samipulizie.it/impresa-pulizie-giulianova-teramo'
        },
        url: 'https://samipulizie.it/',
        priceRange: '€€',
        sameAs: [
          'https://www.facebook.com/profile.php?id=61570800623568',
          'https://www.instagram.com/samy_pulizie/'
        ],
        description:
          'Impresa di pulizie a Pescara; da settembre 2026 operativa anche a Giulianova, Teramo e provincia per uffici, condomini, attività e privati.'
      }
    });
  }

  // ---------- Navigazione ----------
  navigateToService(link: string) {
    this.router.navigateByUrl(link);
  }

  navigateToPreventiviSito() {
    this.router.navigateByUrl('/preventivo');
  }

  navigateToLavoraConNoi() {
    this.router.navigateByUrl('/lavora-con-noi');
  }

  navigateToServices() {
    if (!this.isBrowser) return;
    const el = document.getElementById('servizi');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

ngOnInit(): void {
    this.setupSeo();
  }

  trackByTitle(_: number, item: { title: string }) {
    return item.title;
  }

  trackByPartnerName(_: number, item: Partner) {
    return item.name;
  }
}
